import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"
import { Turbo } from "@hotwired/turbo-rails"
import { dragLiftOptions } from "../lib/drag_lift"

export default class extends Controller {
  static targets = ["seatsContainer", "expandIcon", "seatSlot", "number"]
  // locked: a frozen event or a co-host who can only view; no seat takes a drop
  static values = { id: Number, eventId: Number, capacity: Number, assignedCount: Number, locked: Boolean }
  // The seat of a dragged guest's plus-one, who moves with them
  static partnerClasses = ['ring-2', 'ring-teal-400', 'ring-offset-1']

  connect() {
    this.expanded = false
    // Track seat slot sortable instances. Keep them across a reconnect: the
    // card is re-connected whenever the tables list drags it to a new spot.
    this.seatSlotSortables ||= []
    // Remembered so the siblings can be renumbered after this card is removed
    this.list = this.element.closest('#tables-list')
    this.renumber()
    // Auto-expand tables on connect for better UX
    this.autoExpand()

    // Initialize the seat slots after a short delay so the DOM is ready and visible
    if (!this.lockedValue) setTimeout(() => this.initializeSeatSlots(), 100)
  }

  autoExpand() {
    // Automatically expand tables that have assigned guests OR have available seats
    const assignedCount = this.assignedCountValue || 0
    const capacity = this.capacityValue

    if (assignedCount > 0 || assignedCount < capacity) {
      this.expanded = true
      if (this.hasSeatsContainerTarget) {
        this.seatsContainerTarget.classList.remove('hidden')
        if (this.hasExpandIconTarget) {
          this.expandIconTarget.classList.add('rotate-180')
        }
      }
    }
  }

  initializeSeatSlots() {
    // Make individual seat slots droppable
    if (this.seatSlotTargets.length === 0) {
      console.error('No seat slots found')
      return
    }

    this.seatSlotTargets.forEach((slot) => {
      // Skip if this slot already has a sortable instance
      if (slot.classList.contains('sortable-initialized')) return

      const slotSortable = Sortable.create(slot, {
        ...dragLiftOptions,
        // Only take guests (`put: true` would also accept table cards dragged
        // out of the tables list), and only into a seat that is still free.
        group: {
          name: 'guests',
          put: (to, from) => from.options.group.name === 'guests' && this.seatIsFree(to.el),
          pull: true
        },
        draggable: '[data-controller~="seated-guest"]',

        onStart: (evt) => this.markPartner(evt.item),
        onEnd: () => this.clearPartner(),

        onAdd: (evt) => {
          const revert = this.revertDrop(evt)

          // Belt and braces for the put() guard above
          if (!this.seatIsFree(slot, evt.item)) {
            revert()
            return
          }

          const card = evt.item
          const guestId = card.dataset.guestCardIdValue || card.dataset.seatedGuestGuestIdValue

          this.assignGuest(guestId, slot.dataset.seatNumber, this.isPlusOneCard(card), revert)
        },

        onRemove: (evt) => {
          document.dispatchEvent(new CustomEvent('guest:moved'))
        }
      })

      // Mark slot as initialized to prevent duplicates
      slot.classList.add('sortable-initialized')

      // Store reference for cleanup
      this.seatSlotSortables.push({ element: slot, sortable: slotSortable })
    })
  }

  toggleExpanded() {
    this.expanded = !this.expanded

    if (this.expanded) {
      this.seatsContainerTarget.classList.remove('hidden')
      this.expandIconTarget.classList.add('rotate-180')
    } else {
      this.seatsContainerTarget.classList.add('hidden')
      this.expandIconTarget.classList.remove('rotate-180')
    }
  }

  // The circled number is the card's 1-based place in the list. Cards are
  // reordered by drag, appended and removed without a re-render of the
  // others, so every card recounts the whole list whenever it (re)connects
  // or leaves.
  renumber() {
    if (!this.list) return
    this.list.querySelectorAll('[data-table-card-target="number"]').forEach((el, index) => {
      el.textContent = index + 1
    })
  }
  
  // An unassigned card (guest-card) or a seated one (seated-guest) for the
  // guest's plus-one rather than the guest
  isPlusOneCard(card) {
    return card.dataset.guestCardPlusOneValue === 'true' || card.dataset.seatedGuestPlusOneValue === 'true'
  }

  // A seat is free when nothing sits in it (a guest or a plus-one). The one
  // being dragged doesn't count: while in flight it carries Sortable's
  // placeholder class, and by the time onAdd fires it has already been
  // dropped into the slot (`ignore`).
  seatIsFree(slot, ignore = null) {
    const seated = Array.from(slot.querySelectorAll('[data-controller~="seated-guest"]'))
    return !seated.some(el => el !== ignore && !el.classList.contains('drag-placeholder'))
  }

  // Returns a function that puts the dropped item back where it came from,
  // for when the server refuses the assignment.
  revertDrop(evt) {
    const { from, item, oldIndex } = evt
    return () => from.insertBefore(item, from.children[oldIndex] || null)
  }

  // While a seated guest is dragged, rings their seated plus-one, at any
  // table: with "Keep plus-ones with their guest" on, dropping the guest
  // anywhere brings the plus-one beside them (AssignGuestSeatService).
  // Dragging the plus-one moves only them, and with the switch off no one follows.
  markPartner(card) {
    this.clearPartner()
    if (card.dataset.seatedGuestPlusOneValue !== 'false') return
    if (document.getElementById('plus-one-pairing-setting')?.dataset.keepPlusOnes === 'false') return

    const guestId = card.dataset.seatedGuestGuestIdValue
    const partner = document.querySelector(
      `[data-controller~="seated-guest"][data-seated-guest-guest-id-value="${guestId}"][data-seated-guest-plus-one-value="true"]`
    )
    const slot = partner?.closest('[data-seat-number]')
    if (!slot) return

    const label = document.createElement('span')
    label.className = 'absolute -top-2 left-1/2 -translate-x-1/2 whitespace-nowrap px-1.5 py-0.5 rounded-full bg-teal-500 text-white text-[10px] font-semibold shadow-sm pointer-events-none'
    label.textContent = 'Moves with'
    slot.appendChild(label)
    slot.classList.add(...this.constructor.partnerClasses)
    this.partnerMark = { slot, label }
  }

  clearPartner() {
    if (!this.partnerMark) return

    const { slot, label } = this.partnerMark
    label.remove()
    slot.classList.remove(...this.constructor.partnerClasses)
    this.partnerMark = null
  }

  // Seats one person in this table's seat: the guest, or (plusOne) the
  // guest's plus-one. While plus-ones are kept with their guest, the guest's
  // plus-one follows them to the seat beside them; a plus-one dropped alone
  // moves alone.
  assignGuest(guestId, seatNumber, plusOne = false, revert = () => location.reload()) {
    const url = `/events/${this.eventIdValue}/event_tables/${this.idValue}/assign_seat`

    this.saveDrop(url, 'POST', { guest_id: guestId, seat_number: seatNumber, plus_one: plusOne }, revert)
      .then(saved => { if (saved) document.dispatchEvent(new CustomEvent('guest:assigned')) })
  }

  // Sends a drop to the server and applies the Turbo Stream it answers with.
  // Resolves true when the drop was saved. When the server refuses it (seat
  // taken, table full, ...), the card goes back where it came from and the
  // server's flash message is shown.
  saveDrop(url, method, body, revert) {
    const token = document.querySelector('[name="csrf-token"]').content

    return fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': token,
        'Accept': 'text/vnd.turbo-stream.html'
      },
      body: JSON.stringify(body)
    }).then(response => {
      if (!response.ok) {
        console.error(`Drop refused: ${method} ${url}`)
        revert()
        return response.text().then(html => {
          if (html.includes('<turbo-stream')) Turbo.renderStreamMessage(html)
          return false
        })
      }
      return response.text().then(html => {
        Turbo.renderStreamMessage(html)
        return true
      })
    }).catch(error => {
      console.error('Error saving drop:', error)
      location.reload() // Reload to restore proper state
      return false
    })
  }

  disconnect() {
    // Reordering the tables list moves this card in the DOM, which disconnects
    // and reconnects the controller while the drag is still in progress.
    // Sortable.destroy() resets the library's global drag state, which would
    // strand the table mid-drag (faded, never saved), so only tear down when
    // the card has actually left the page.
    if (this.element.isConnected) return

    this.clearPartner()
    this.renumber()

    // Clean up seat slot sortables if they exist
    if (this.seatSlotSortables && this.seatSlotSortables.length > 0) {
      this.seatSlotSortables.forEach(({ element, sortable }) => {
        sortable.destroy()
        element.classList.remove('sortable-initialized')
      })
      this.seatSlotSortables = []
    }
  }
}